import { apiClient } from '../apiClient';

/**
 * How a problem was worked on, as opposed to whether it was solved.
 *
 * Counters here are DELTAS since the previous heartbeat — `code_chars` is the
 * exception, describing the code as it currently stands. Sending deltas means a
 * heartbeat lost to a flaky campus network costs one interval of accuracy
 * instead of corrupting the running total.
 */
export interface AttemptHeartbeat {
  problem_slug: string;
  language: string;
  active_secs: number;
  typed_chars: number;
  /** One entry per paste, each its size in characters. */
  pastes: number[];
  runs: number;
  submits: number;
  code_chars: number;
  /** A claim, not a fact: the server confirms it against an accepted submission. */
  solved: boolean;
}

export type AttemptSignal = 'self_solved' | 'assisted' | 'pasted' | 'insufficient';

export interface Attempt {
  id: string;
  user_id: string;
  problem_id: string;
  language?: string;
  started_at: string;
  last_active_at: string;
  first_run_at?: string;
  solved_at?: string;
  active_secs: number;
  typed_chars: number;
  paste_count: number;
  paste_chars: number;
  largest_paste_chars: number;
  run_count: number;
  submit_count: number;
  final_code_chars: number;
}

export interface AttemptHeartbeatResponse {
  attempt: Attempt;
  signal: AttemptSignal;
  solved: boolean;
}

export interface AttemptThresholds {
  pasted_ratio: number;
  assisted_ratio: number;
  composed_ratio: number;
  min_code_chars: number;
  min_active_secs: number;
}

export const attemptsApi = {
  heartbeat: async (body: AttemptHeartbeat) => {
    const { data } = await apiClient.post<AttemptHeartbeatResponse>(
      '/api/v1/attempts/heartbeat',
      body,
    );
    return data;
  },
  getThresholds: async () => {
    const { data } = await apiClient.get<AttemptThresholds>('/api/v1/attempts/thresholds');
    return data;
  },
};
